/**
 * @fileoverview Shared presentation-only Defensible brand mark.
 * @module ui-common/components/DefensibleLogo
 *
 * Responsibilities: - Render the Defensible shield and wordmark - Support compact, stacked, and themed variants
 * Dependencies:     - React JSX runtime
 * Architecture:     - Shared presentation component with no application state or data access
 * Notes:            - Used on authentication surfaces when no department logo is configured
 *
 * @component DefensibleLogo
 * Purpose:     Renders the Defensible product mark with an optional subtitle.
 * Props:       size, subtitle, color, subtitleColor, stacked, className
 * Behavior:    Inherits the current text color unless explicit colors are provided.
 * Data Source: local (presentation props only)
 * Constraints: no data access, side effects, or authentication behavior.
 * Performance: inline SVG and styles only.
 *
 * @reusable
 * Usage: Render in shared application shells and authentication surfaces.
 * Rules: presentation-only; callers provide theme and subtitle values.
 */

/**
 * @contract DefensibleLogoProps
 * Purpose: Defines visual options for the Defensible brand mark.
 * Fields: size, subtitle, color, subtitleColor, stacked, className
 * Rules: size defaults to `md`; explicit colors override inherited text color.
 * Sync: none — presentation-only.
 */
export interface DefensibleLogoProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  subtitle?: string;
  color?: string;
  subtitleColor?: string;
  stacked?: boolean;
  className?: string;
}

const SIZE = {
  xs: { shield: 16, def: '0.7rem', sub: '0.5rem', gap: 6 },
  sm: { shield: 22, def: '0.9rem', sub: '0.55rem', gap: 8 },
  md: { shield: 28, def: '1.1rem', sub: '0.6rem', gap: 10 },
  lg: { shield: 44, def: '1.6rem', sub: '0.75rem', gap: 12 },
} as const;

export default function DefensibleLogo({
  size = 'md',
  subtitle,
  color,
  subtitleColor,
  stacked = false,
  className = '',
}: DefensibleLogoProps) {
  const dimensions = SIZE[size];
  const mainColor = color ?? 'currentColor';
  const subColor = subtitleColor ?? color ?? 'currentColor';

  const shield = (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: dimensions.shield, height: dimensions.shield, flexShrink: 0, opacity: 0.9 }}
    >
      <path
        d="M12 2L3 6.5V12c0 4.97 3.76 9.63 9 10.93C17.24 21.63 21 16.97 21 12V6.5L12 2Z"
        fill="currentColor"
        fillOpacity={0.12}
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinejoin="round"
      />
      <path
        d="M9 12l2 2 4-4"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const wordmark = (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1, gap: 2 }}>
      <span
        style={{
          fontFamily: "Georgia, 'Times New Roman', 'Palatino Linotype', serif",
          fontStyle: 'italic',
          fontWeight: 700,
          fontSize: dimensions.def,
          color: mainColor,
          letterSpacing: '-0.01em',
          whiteSpace: 'nowrap',
          lineHeight: 1.1,
        }}
      >
        Defensible
      </span>
      {subtitle && (
        <span
          style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            fontWeight: 500,
            fontSize: dimensions.sub,
            color: subColor,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            opacity: 0.65,
          }}
        >
          {subtitle}
        </span>
      )}
    </div>
  );

  return stacked ? (
    <div
      className={className}
      style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: dimensions.gap / 2 }}
    >
      {shield}
      {wordmark}
    </div>
  ) : (
    <div className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: dimensions.gap }}>
      {shield}
      {wordmark}
    </div>
  );
}
